// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/architecture/python-fastapi-architecture.md
// Refer to: docs/manual/02-voice-process.md

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Container,
  Snackbar,
  Stack,
} from '@mui/material';
import {
  Close,
  ContentCopy,
  Download,
  VolumeUp,
  CheckCircle,
  Error,
  Info,
  ExpandMore,
  EmojiEmotions,
  ThumbUp,
  ThumbDown,
  Share,
} from '@mui/icons-material';
import { apiClient, API_ENDPOINTS } from '../config/api';
import AnalysisResults from './AnalysisResults';
import VoiceDiagnosticLogSection from './VoiceDiagnosticLogSection';
import { mapVoiceShareBundleToDetailState } from '../utils/mapVoiceShareBundle';
import { buildVoiceProcessTaskDeepLink } from '../utils/voiceProcessTaskDeepLink';

/** Caption under header icon buttons (modal). */
function LinkHintText({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        fontWeight: 600,
        color: 'primary.main',
        textAlign: 'right',
        display: 'block',
        lineHeight: 1.2,
      }}
    >
      {children}
    </Typography>
  );
}

/** Split transcript into sentences for reference linking (1-based indices). */
function getTranscriptSentences(text) {
  if (!text || typeof text !== 'string') return [];
  return text
    .split(/[.!?\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const VoiceDetailModal = ({
  open,
  onClose,
  fileId,
  fileName,
  onListRefreshSuggested,
  displayMode = 'modal',
  readOnly = false,
  preloadedBundle = null,
}) => {
  const [tabValue, setTabValue] = useState(1); // Default to Analysis tab
  const [analysisMutatedSinceOpen, setAnalysisMutatedSinceOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transcriptionData, setTranscriptionData] = useState(null);
  const [transcriptionUnavailable, setTranscriptionUnavailable] = useState(null); // Message when API returns success: false
  const [metadata, setMetadata] = useState(null);
  const [metadataUnavailable, setMetadataUnavailable] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisDataKey, setAnalysisDataKey] = useState(0);
  const [scrollToSentence, setScrollToSentence] = useState(null);
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [shareSnackbar, setShareSnackbar] = useState({ open: false, message: '', isError: false });

  const isFullPage = displayMode === 'fullPage';

  useEffect(() => {
    if (preloadedBundle) {
      const m = mapVoiceShareBundleToDetailState(preloadedBundle);
      setLoading(false);
      setError(null);
      setTranscriptionData(m.transcriptionData);
      setTranscriptionUnavailable(m.transcriptionUnavailable);
      setMetadata(m.metadata);
      setMetadataUnavailable(m.metadataUnavailable);
      setAnalysisData(m.analysisData);
      return;
    }
    const shouldFetch = Boolean(fileId && (isFullPage || open));
    if (shouldFetch) {
      fetchAllData();
    }
  }, [open, fileId, preloadedBundle, displayMode]);

  useEffect(() => {
    if (open || isFullPage) {
      setAnalysisMutatedSinceOpen(false);
    }
  }, [open, isFullPage, fileId]);

  const handleCloseModal = () => {
    if (analysisMutatedSinceOpen && typeof onListRefreshSuggested === 'function') {
      onListRefreshSuggested();
    }
    setAnalysisMutatedSinceOpen(false);
    onClose();
  };

  const handleCopyTeamVoiceLink = async () => {
    if (!fileId || readOnly || isFullPage) return;
    const url = buildVoiceProcessTaskDeepLink(fileId);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setShareSnackbar({
        open: true,
        message:
          'Link copied. Signed-in teammates with access can open Voice Process and this transcription directly.',
        isError: false,
      });
    } catch {
      setShareSnackbar({
        open: true,
        message: 'Could not copy to clipboard. Check browser permissions.',
        isError: true,
      });
    }
  };

  const handleGenerateShareLink = async () => {
    if (!fileId || readOnly) return;
    setShareLinkLoading(true);
    try {
      const res = await apiClient.post(
        `${API_ENDPOINTS.VOICE_TASK_SHARE_LINK}/${fileId}/share-link`
      );
      const raw = res.data?.token;
      const path = res.data?.share_url_path || `/share/voice/${raw}`;
      const base = (import.meta.env.VITE_FRONTEND_URL || window.location.origin || '').replace(/\/$/, '');
      const full = `${base}${path.startsWith('/') ? path : `/${path}`}`;
      await navigator.clipboard.writeText(full);
      setShareSnackbar({
        open: true,
        message: 'One-time link copied to clipboard. It can be opened only once.',
        isError: false,
      });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to generate link';
      setShareSnackbar({ open: true, message: String(msg), isError: true });
    } finally {
      setShareLinkLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue !== 0 || scrollToSentence == null) return;
    const id = `sentence-${scrollToSentence}`;
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setScrollToSentence(null);
    }, 300);
    return () => clearTimeout(timer);
  }, [tabValue, scrollToSentence]);

  const handleReferenceTranscription = (sentenceNumbers) => {
    if (!Array.isArray(sentenceNumbers) || sentenceNumbers.length === 0) return;
    setTabValue(0);
    setScrollToSentence(sentenceNumbers[0]);
  };

  const refetchAnalysis = React.useCallback(async () => {
    if (!fileId) {
      console.warn('[VoiceDetailModal] Cannot refetch: fileId is missing');
      return;
    }
    try {
      console.log('[VoiceDetailModal] Refetching analysis after override...', { fileId });
      const analysisResponse = await apiClient.get(`${API_ENDPOINTS.VOICE_TASKS}/${fileId}/analysis`);
      if (analysisResponse.data?.success && analysisResponse.data?.analysis) {
        const newData = analysisResponse.data;
        console.log('[VoiceDetailModal] Analysis refetched successfully:', {
          qa_metric_overrides: Object.keys(newData.analysis.qa_metric_overrides || {}),
          ollama_qa_score: newData.analysis.ollama_qa_score,
          metrics_count: newData.analysis.ollama_qa_metric_scores?.length,
        });
        // Force new object reference to trigger re-render
        setAnalysisData({ ...newData });
        setAnalysisDataKey(prev => prev + 1); // Force re-render by changing key
      } else {
        console.warn('[VoiceDetailModal] Refetch response missing analysis:', analysisResponse.data);
      }
    } catch (err) {
      console.error('[VoiceDetailModal] Failed to refetch analysis after override:', err);
      console.error('[VoiceDetailModal] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
    }
  }, [fileId]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    setTranscriptionUnavailable(null);
    setMetadataUnavailable(false);
    setTranscriptionData(null);
    setMetadata(null);
    setAnalysisData(null);
    try {
      // Fetch transcription, metadata, and analysis in parallel (read from DB only; no heavy processing)
      const [transcriptionResponse, metadataResponse, analysisResponse] = await Promise.all([
        apiClient.get(`${API_ENDPOINTS.VOICE_TRANSCRIPTION}/${fileId}/transcription`),
        apiClient.get(`${API_ENDPOINTS.VOICE_METADATA}/${fileId}/metadata`),
        apiClient.get(`${API_ENDPOINTS.VOICE_TASKS}/${fileId}/analysis`).catch(() => ({ data: {} })),
      ]);

      const transcriptionPayload = transcriptionResponse.data;
      if (transcriptionPayload.success && transcriptionPayload.transcription) {
        const t = transcriptionPayload.transcription;
        setTranscriptionData({
          transcription_text: t.raw_text,
          cleaned_text: t.cleaned_text,
          enhanced_text: t.enhanced_text,
          ollama_enhanced_text: t.ollama_enhanced_text,
          confidence_score: t.confidence,
          processing_time: (t.processing_time ?? 0) * 1000, // Convert s to ms
          speech_ratio: null,
          quality_score: null,
          original_language: t.language_detected,
          word_timestamps: t.word_timestamps,
          quality_warning: t.quality_warning || null
        });
      } else {
        setTranscriptionUnavailable(
          transcriptionPayload.message || 'Transcription not yet available for this task.'
        );
      }

      const metaData = metadataResponse.data;
      if (metaData.success && metaData.metadata) {
        setMetadata({
          status: transcriptionPayload.status ?? metaData.task_status,
          qa_criteria_id: null,
          ...metaData.metadata
        });
      } else {
        setMetadataUnavailable(true);
      }

      if (analysisResponse.data?.success && analysisResponse.data?.analysis) {
        setAnalysisData(analysisResponse.data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      const detail = err.response?.data?.detail;
      let message = 'Failed to load data. Please try again.';
      if (typeof detail === 'string') message = detail;
      else if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        message = typeof first?.msg === 'string' ? first.msg : String(first);
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const handleDownloadTranscription = () => {
    if (!transcriptionData) return;
    
    const content = `Transcription Report\n` +
      `File: ${fileName}\n` +
      `Date: ${new Date().toLocaleDateString()}\n\n` +
      `Raw Transcription:\n${transcriptionData.transcription_text || 'N/A'}\n\n` +
      `Cleaned Transcription:\n${transcriptionData.cleaned_text || 'N/A'}\n\n` +
      `Enhanced Transcription (Ollama):\n${transcriptionData.ollama_enhanced_text || 'N/A'}\n\n` +
      `Processing Time: ${transcriptionData.processing_time || 0}ms\n` +
      `Confidence Score: ${transcriptionData.confidence_score || 0}\n` +
      `Language: ${transcriptionData.original_language || 'Unknown'}`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}_transcription.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'ready_to_analyse':
        return 'info';
      case 'processing':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getLanguageName = (code) => {
    const languages = {
      'en': 'English',
      'th': 'Thai',
      'zh': 'Chinese',
      'ja': 'Japanese',
      'ko': 'Korean',
    };
    return languages[code] || (code ? code.toUpperCase() : 'Unknown');
  };

  if (!isFullPage && !open) return null;

  const titleActions = (
    <Box display="flex" alignItems="flex-start" gap={1}>
      {!readOnly && !isFullPage && fileId && (
        <Stack direction="row" alignItems="flex-end" spacing={1.5}>
          <Stack alignItems="flex-end" spacing={0.5}>
            <Tooltip title="Copy link for signed-in teammates with access. Opens this transcription on Voice Process (no public token).">
              <span>
                <IconButton
                  onClick={handleCopyTeamVoiceLink}
                  disabled={loading}
                  size="small"
                  color="primary"
                  aria-label="Copy link for teammates"
                >
                  <ContentCopy />
                </IconButton>
              </span>
            </Tooltip>
            <LinkHintText>Copy Link</LinkHintText>
          </Stack>
          <Stack alignItems="flex-end" spacing={0.5}>
            <Tooltip title="Generate a public one-time link (single use). Copied to clipboard.">
              <span>
                <IconButton
                  onClick={handleGenerateShareLink}
                  disabled={shareLinkLoading || loading}
                  size="small"
                  color="primary"
                  aria-label="Generate one-time share link"
                >
                  <Share />
                </IconButton>
              </span>
            </Tooltip>
            <LinkHintText>One-time Link</LinkHintText>
          </Stack>
        </Stack>
      )}
      {!isFullPage && (
        <IconButton onClick={handleCloseModal} size="small" aria-label="Close" sx={{ mt: 0.25 }}>
          <Close />
        </IconButton>
      )}
    </Box>
  );

  const headerBlock = (
    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
      <Box>
        <Typography variant="h6" component="div">
          Transcription Details - {fileName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
          ({fileId})
        </Typography>
      </Box>
      {titleActions}
    </Box>
  );

  const scrollableContentSx = {
    '& > *:last-child:empty': { display: 'none' },
    '& > div:empty': { display: 'none' },
    '& > span:empty': { display: 'none' },
    '& > *': {
      '&:empty::before': { content: 'none' },
      '&:empty::after': { content: 'none' },
    },
  };

  const detailBody = (
    <>
        {/* Tabs */}
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="📝 Transcription" />
          <Tab 
            label="📊 Analysis" 
            disabled={!analysisData || !analysisData.analysis}
          />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading data...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box>
            {/* Tab 0: Transcription – show info when transcription not yet available */}
            {tabValue === 0 && transcriptionUnavailable && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {transcriptionUnavailable}
                </Alert>
                {metadata && (
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      File Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          File ID: {fileId}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Status:{' '}
                          <Chip
                            label={metadata.status}
                            color={getStatusColor(metadata.status)}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                )}
                {metadataUnavailable && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Metadata not yet available for this task.
                  </Alert>
                )}
              </Box>
            )}
            {/* Tab 0: Full transcription content when data is available */}
            {tabValue === 0 && transcriptionData && (
              <Box>
            {/* File Information */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                File Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    File ID: {fileId}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Language: {getLanguageName(transcriptionData.original_language)}
                  </Typography>
                </Grid>
                {metadata && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        QA Criteria: {metadata.qa_criteria_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Status:
                        <Chip
                          label={metadata.status}
                          color={getStatusColor(metadata.status)}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                    </Grid>
                  </>
                )}
                {metadataUnavailable && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Metadata not yet available for this task.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Processing Statistics */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Processing Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {transcriptionData.processing_time ? 
                        `${(transcriptionData.processing_time / 1000).toFixed(1)}s` : 
                        'N/A'
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Processing Time
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="secondary">
                      {transcriptionData.confidence_score !== null && transcriptionData.confidence_score !== undefined ? 
                        `${(transcriptionData.confidence_score * 100).toFixed(1)}%` : 
                        'N/A'
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Confidence Score
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="info.main">
                      {transcriptionData.original_language ? 
                        getLanguageName(transcriptionData.original_language) : 
                        'N/A'
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Language
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Metadata Info */}
              {metadata && (
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Duration: {metadata.duration ? `${metadata.duration.toFixed(1)}s` : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Sample Rate: {metadata.sample_rate ? `${metadata.sample_rate} Hz` : 'N/A'}
                      </Typography>
                    </Grid>
                    {metadata.silence_ratio !== null && metadata.silence_ratio !== undefined && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Silence Ratio: {(metadata.silence_ratio * 100).toFixed(1)}%
                        </Typography>
                      </Grid>
                    )}
                    {metadata.noise_reduction_applied !== null && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Noise Reduction: {metadata.noise_reduction_applied ? 'Applied' : 'Not Applied'}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Paper>

            {transcriptionData.quality_warning && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {transcriptionData.quality_warning}
              </Alert>
            )}

            {/* Raw Transcription */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Raw Transcription (From Whisper)
                </Typography>
                <Tooltip title="Copy to clipboard">
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopyToClipboard(transcriptionData.transcription_text)}
                  >
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  backgroundColor: 'grey.50',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}
              >
                {transcriptionData.transcription_text || 'No transcription available'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {transcriptionData.transcription_text ? `${transcriptionData.transcription_text.length} characters` : ''}
              </Typography>
            </Paper>

            {/* Cleaned Transcription (numbered sentences for QA reference linking) */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Cleaned Transcription (Filler Words Removed)
                </Typography>
                <Tooltip title="Copy to clipboard">
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopyToClipboard(transcriptionData.cleaned_text)}
                  >
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  backgroundColor: 'success.50',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'success.200',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}
              >
                {(() => {
                  const text = transcriptionData.cleaned_text || transcriptionData.transcription_text || '';
                  const sentences = getTranscriptSentences(text);
                  if (sentences.length === 0) {
                    return (
                      <Typography variant="body1" component="span">
                        {text || 'No cleaned transcription available'}
                      </Typography>
                    );
                  }
                  return sentences.map((sent, i) => (
                    <Typography
                      key={i}
                      id={`sentence-${i + 1}`}
                      component="span"
                      variant="body1"
                      sx={{ display: 'inline' }}
                    >
                      {i > 0 ? ' ' : ''}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                        [{i + 1}]
                      </Typography>
                      {sent}.
                    </Typography>
                  ));
                })()}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {transcriptionData.cleaned_text ? `${transcriptionData.cleaned_text.length} characters` : ''}
              </Typography>
            </Paper>

            {/* ChatGPT Enhanced Transcription removed - ChatGPT deprecated */}

            {/* Enhanced Transcription - Ollama (show whenever present, e.g. when using Ollama only) */}
            {transcriptionData.ollama_enhanced_text && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Enhanced Transcription (Ollama Improved)
                  </Typography>
                  <Tooltip title="Copy to clipboard">
                    <IconButton 
                      size="small" 
                      onClick={() => handleCopyToClipboard(transcriptionData.ollama_enhanced_text)}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    backgroundColor: 'info.50',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'info.200',
                    maxHeight: '200px',
                    overflow: 'auto'
                  }}
                >
                  {transcriptionData.ollama_enhanced_text}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {transcriptionData.ollama_enhanced_text.length} characters • Free & Local Processing
                </Typography>
              </Paper>
            )}

            {/* Word Timestamps */}
            {transcriptionData.word_timestamps && transcriptionData.word_timestamps.length > 0 && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Word Timestamps ({transcriptionData.word_timestamps.length} segments)
                </Typography>
                <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
                  {transcriptionData.word_timestamps.slice(0, 10).map((segment, index) => (
                    <Box key={index} sx={{ mb: 1, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        [{segment.start?.toFixed(1)}s - {segment.end?.toFixed(1)}s]
                      </Typography>
                      <Typography variant="body2">
                        {segment.text}
                      </Typography>
                    </Box>
                  ))}
                  {transcriptionData.word_timestamps.length > 10 && (
                    <Typography variant="caption" color="text.secondary">
                      ... and {transcriptionData.word_timestamps.length - 10} more segments
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}
              </Box>
            )}

            {/* Tab 2: Analysis Results */}
            {tabValue === 1 && (
              <AnalysisResults 
                key={`analysis-${analysisDataKey}-${fileId}`}
                analysisData={analysisData} 
                onReferenceTranscription={handleReferenceTranscription}
                transcriptSentences={transcriptionData ? getTranscriptSentences(transcriptionData.cleaned_text || transcriptionData.transcription_text || '') : []}
                taskId={fileId}
                readOnly={readOnly}
                onAnalysisUpdated={() => {
                  console.log('[VoiceDetailModal] onAnalysisUpdated called, refetching...');
                  setAnalysisMutatedSinceOpen(true);
                  refetchAnalysis();
                }}
              />
            )}
            <VoiceDiagnosticLogSection taskId={fileId} readOnly={readOnly} />
          </Box>
        )}
    </>
  );

  const footerButtons = (
    <>
      {!isFullPage && (
        <Button onClick={handleCloseModal}>
          Close
        </Button>
      )}
      {transcriptionData && !readOnly && (
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleDownloadTranscription}
        >
          Download Transcription
        </Button>
      )}
    </>
  );

  const fullPageFooterVisible =
    isFullPage && (!readOnly && transcriptionData);

  const snackbarEl = (
    <Snackbar
      open={shareSnackbar.open}
      autoHideDuration={10000}
      onClose={() => setShareSnackbar((s) => ({ ...s, open: false }))}
      message={shareSnackbar.message}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />
  );

  if (isFullPage) {
    return (
      <>
        <Box sx={{ width: '100%', minHeight: '100%', bgcolor: '#ffffff' }}>
          <Container maxWidth="lg" sx={{ py: 2, pb: 4 }}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              {headerBlock}
            </Paper>
            <Box sx={scrollableContentSx}>
              {detailBody}
            </Box>
            {fullPageFooterVisible && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                {footerButtons}
              </Box>
            )}
          </Container>
        </Box>
        {snackbarEl}
      </>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' },
        }}
      >
        <DialogTitle>{headerBlock}</DialogTitle>
        <DialogContent dividers sx={scrollableContentSx}>
          {detailBody}
        </DialogContent>
        <DialogActions>{footerButtons}</DialogActions>
      </Dialog>
      {snackbarEl}
    </>
  );
};

export default VoiceDetailModal;
